import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { RootState, AppDispatch } from '../../app/store'
import { setLocalSelectedTable } from '../../features/tables/tablesSlice'
import TableCard from './TableCard'

const DataTables: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()

  const {
    tables,
    activeTab,
    selectedTable,
    isLoadingTables
  } = useSelector((state: RootState) => state.tables)

  const isRTL = useSelector((state: RootState) => state.settings.settings.language === 'ar')

  const handleTableSelect = (tableId: string | number) => {
    // Allow selection in both tabs
    if (activeTab === 0) {
      // Allow selection in "Choose Table" tab for all tables
      dispatch(setLocalSelectedTable(Number(tableId)))
    } else if (activeTab === 1) {
      // Allow selection in "Free Table" tab for occupied tables
      dispatch(setLocalSelectedTable(Number(tableId)))
    }
  }

  // Filter tables based on active tab
  const filteredTables = tables.filter((table) => {
    if (activeTab === 0) {
      // Choose Table tab - show available tables and occupied tables (Used)
      return table.status === 'available' || table.status === 'occupied' || !table.status
    }

    if (activeTab === 1) {
      // Free Table tab - show occupied tables (Used)
      return table.status === 'occupied'
    }

    // VIP tab - show tables marked as VIP
    return Boolean(table.isVIP)
  })

  if (isLoadingTables) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-flutter-button mx-auto mb-4"></div>
          <p className="text-base font-medium text-gray-600 font-['IBM Plex Sans Arabic']">
            {t('table.loading_tables')}
          </p>
        </div>
      </div>
    )
  }

  const emptyTitle =
    activeTab === 0
      ? t('table.no_available_tables')
      : activeTab === 1
        ? t('table.no_occupied_tables')
        : t('table.no_vip_tables')

  const emptyDescription =
    activeTab === 0
      ? t('table.all_tables_occupied')
      : activeTab === 1
        ? t('table.all_tables_available')
        : t('table.all_vip_tables_unavailable')

  if (filteredTables.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-600 mb-2 font-['IBM Plex Sans Arabic']">
            {emptyTitle}
          </h3>
          <p className="text-sm text-gray-500 font-['IBM Plex Sans Arabic']">
            {emptyDescription}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Tables Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-6">
        {filteredTables.map((table) => (
          <TableCard
            key={table.id}
            table={table as any}
            isSelected={selectedTable === table.id}
            isSelectable={true}
            onSelect={() => handleTableSelect(table.id)}
            isRTL={isRTL}
          />
        ))}
      </div>


    </div>
  )
}

export default DataTables
